import { useState } from 'react';
import { Link, useLang } from '../i18n';
import { featured, articles, categories } from '../data/blogData';
import SEO from '../components/SEO';

export default function BlogPage() {
  const { t } = useLang();
  const [activeCat, setActiveCat] = useState('Todos');
  const [visibleCount, setVisibleCount] = useState(12);
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [newsletterState, setNewsletterState] = useState('idle');

  const submitNewsletter = async (e) => {
    e.preventDefault();
    setNewsletterState('sending');
    try {
      const data = new URLSearchParams({ 'form-name': 'newsletter', email, consentimiento_comercial: marketingConsent ? 'Sí' : 'No' });
      const response = await fetch('/', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: data.toString() });
      if (!response.ok) throw new Error('No se pudo enviar');
      setEmail('');
      setMarketingConsent(false);
      setNewsletterState('sent');
    } catch {
      setNewsletterState('error');
    }
  };

  const filtered = activeCat === 'Todos' ? articles : articles.filter(a => a.cat === activeCat);
  const visibleArticles = filtered.slice(0, visibleCount);

  const changeCategory = (category) => {
    setActiveCat(category);
    setVisibleCount(12);
  };

  return (
    <>
      <SEO
        title={t('Blog para aprender inglés: consejos y recursos', 'Blog per aprendre anglès: consells i recursos')}
        description={t('Consejos gratuitos de pronunciación, gramática, vocabulario y conversación para aprender inglés y ganar confianza al hablar.', 'Consells gratuïts de pronunciació, gramàtica, vocabulari i conversa per aprendre anglès i guanyar confiança en parlar.')}
        keywords="blog inglés, consejos inglés, gramática inglés, pronunciación inglés, phrasal verbs, vocabulario inglés, aprender inglés gratis"
        canonical="https://speakenglishnow.es/blog"
        schema={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Blog de Inglés – Speak English Now",
          "url": "https://speakenglishnow.es/blog",
          "description": "Artículos gratuitos de inglés: pronunciación, gramática, vocabulario y consejos de profesores nativos.",
          "publisher": { "@type": "Organization", "name": "Speak English Now" },
          "inLanguage": "es"
        }}
      />
      {/* HERO */}
      <section className="page-hero">
        <div className="container text-center">
          <span className="section-label">{t('CONSEJOS & RECURSOS', 'CONSELLS & RECURSOS')}</span>
          <h1 className="page-hero-title">
            {t('Blog de Inglés', 'Blog d\'Anglès')}
            <span className="script teal" style={{ display: 'block', fontSize: '52px' }}>Speak English Now</span>
          </h1>
          <p className="page-hero-sub">{t('Artículos, consejos y recursos para mejorar tu inglés día a día.', 'Articles, consells i recursos per millorar el teu anglès dia a dia.')} <span className="teal-text">{t('Aprende con nosotros.', 'Aprèn amb nosaltres.')}</span></p>
        </div>
      </section>

      {/* FEATURED */}
      <section className="section-pad">
        <div className="container">
          <Link to={`/blog/${featured.slug}`} className="blog-featured blog-featured-link" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div className="blog-featured-img">
              <img src={featured.img} alt={featured.title} loading="lazy" decoding="async" />
            </div>
            <div className="blog-featured-content">
              <span className="blog-cat-badge">{featured.cat}</span>
              <h2 className="blog-featured-title">{featured.title}</h2>
              <p className="blog-featured-excerpt">{featured.excerpt}</p>
              <div className="blog-meta">
                <img src={featured.avatar} alt={featured.author} className="blog-avatar" loading="lazy" decoding="async" />
                <span className="blog-author">{featured.author}</span>
                <span className="blog-dot">·</span>
                <span className="blog-date">{featured.date}</span>
                <span className="blog-dot">·</span>
                <span className="blog-read">{featured.readTime}</span>
              </div>
              <span className="btn-primary mt-20" style={{ display: 'inline-flex' }}>{t('Leer Artículo →', 'Llegir l\'article →')}</span>
            </div>
          </Link>
        </div>
      </section>

      {/* ARTICLES GRID */}
      <section className="section-pad bg-light">
        <div className="container">
          <h2 className="section-title text-center">{t('Últimos Artículos', 'Darrers Articles')}</h2>
          <div className="cat-tabs">
            {categories.map(c => (
              <button key={c} className={`cat-tab ${activeCat === c ? 'active' : ''}`} onClick={() => changeCategory(c)}>{c}</button>
            ))}
          </div>
          <div className="blog-grid">
            {visibleArticles.map(a => (
              <Link key={a.slug} to={`/blog/${a.slug}`} className="blog-card blog-card-link" style={{ textDecoration: 'none', color: 'inherit' }}>
                <div className="blog-card-img">
                  <img src={a.cardImg || a.img} alt={a.title} loading="lazy" decoding="async" />
                  <span className="blog-cat-badge">{a.cat}</span>
                </div>
                <div className="blog-card-body">
                  <h3 className="blog-card-title">{a.title}</h3>
                  <p className="blog-card-excerpt">{a.excerpt}</p>
                  <div className="blog-meta">
                    <span className="blog-author">{a.author}</span>
                    <span className="blog-dot">·</span>
                    <span className="blog-read">{a.readTime}</span>
                  </div>
                  <span className="blog-read-more">{t('Leer artículo →', 'Llegir l\'article →')}</span>
                </div>
              </Link>
            ))}
          </div>
          {visibleCount < filtered.length && (
            <div className="blog-load-more">
              <button type="button" className="btn-primary" onClick={() => setVisibleCount(count => count + 12)}>
                {t('Ver más artículos', 'Veure més articles')}
              </button>
              <span>{visibleArticles.length} {t('de', 'de')} {filtered.length}</span>
            </div>
          )}
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="section-pad">
        <div className="container">
          <div className="newsletter-box">
            <div className="newsletter-icon" aria-hidden="true">✉</div>
            <span className="section-label">{t('IDEAS PARA SEGUIR APRENDIENDO', 'IDEES PER CONTINUAR APRENENT')}</span>
            <h2 className="newsletter-title">{t('Un poco de inglés en tu bandeja de entrada', 'Una mica d\'anglès a la teva safata d\'entrada')}</h2>
            <p className="newsletter-sub">{t('Consejos prácticos y nuevos artículos de Speak English Now. Sin ruido, solo contenido útil.', 'Consells pràctics i nous articles de Speak English Now. Sense soroll, només contingut útil.')}</p>
            <form name="newsletter" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" className="newsletter-form" onSubmit={submitNewsletter}>
              <input type="hidden" name="form-name" value="newsletter" />
              <p hidden><label>No rellenes este campo: <input name="bot-field" /></label></p>
              <div className="newsletter-fields"><label className="sr-only" htmlFor="newsletter-email">{t('Tu correo electrónico', 'El teu correu electrònic')}</label><input id="newsletter-email" name="email" type="email" placeholder={t('Tu correo electrónico', 'El teu correu electrònic')} value={email} onChange={e => setEmail(e.target.value)} required /><button type="submit" className="btn-teal-full" disabled={newsletterState === 'sending'}>{newsletterState === 'sending' ? t('Enviando…', 'Enviant…') : t('Quiero recibirlo →', 'Vull rebre\'l →')}</button></div>
              <label className="legal-consent newsletter-consent"><input name="consentimiento_comercial" type="checkbox" required checked={marketingConsent} onChange={e => setMarketingConsent(e.target.checked)} /><span>{t('Quiero recibir consejos y novedades de Speak English Now y he leído la', 'Vull rebre consells i novetats de Speak English Now i he llegit la')} <Link to="/politica-privacidad">{t('Política de Privacidad', 'Política de Privacitat')}</Link>.</span></label>
            </form>
            {newsletterState === 'sent' ? <p className="newsletter-note newsletter-success">{t('¡Gracias! Ya te hemos apuntado.', 'Gràcies! Ja t\'hem apuntat.')}</p> : newsletterState === 'error' ? <p className="newsletter-note form-error">{t('No se pudo completar la suscripción. Inténtalo de nuevo.', 'No s\'ha pogut completar la subscripció. Torna-ho a provar.')}</p> : <p className="newsletter-note">{t('Sin spam. Cancela cuando quieras.', 'Sense spam. Cancel·la quan vulguis.')}</p>}
          </div>
        </div>
      </section>
    </>
  );
}
